<!--
  LegalAiFilesTable Component
  
  Displays a table of uploaded legal AI files with search, sort, filter, and pagination.
  Uses ThemedTable component from theme v1 for consistent styling and functionality.
  
  Features:
  - Status filtering (queued, processing, done, failed)
  - Search across file name, status, and dates
  - Sortable columns
  - Pagination (10 items per page)
  - Clickable file names for completed files
  - Status badges with color coding
  - Light/dark theme support
  
  Usage:
  <legal-ai-files-table ref="filesTable"></legal-ai-files-table>
  
  Methods:
  - refresh(): Reload table data from API
  
  Events:
  None (uses ThemedTable's internal events)
  
  API Endpoint:
  GET /lease-ai-files/all-data
  Expected response: { success: true, jobs: [...], total: number }
-->
<template>
  <div class="legal-ai-files-table">
    <!-- Toolbar -->
    <div class="legal-ai-files-table__toolbar">
      <div class="legal-ai-files-table__title-section">
        <h2 class="legal-ai-files-table__title">Legal AI Files</h2>
      </div>
      
      <div class="legal-ai-files-table__actions">
        <!-- Search -->
        <themed-search
          v-model="searchQuery"
          placeholder="Search files..."
          aria-label="Search legal AI files"
        />
        

       
        
        <!-- Status Filter -->
        <div class="status-filter-container">
          <label for="status_filter" class="filter-label">Status</label>
          <select 
            id="status_filter" 
            v-model="statusFilter" 
            class="filter-select"
            @change="handleStatusFilterChange"
          >
            <option value="">All</option>
            <option value="queued">Queued</option>
            <option value="processing">Processing</option>
            <option value="done">Done</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        
        <!-- Display Count Dropdown -->
        <div class="themed-dropdown">
          <themed-button 
            @click="toggleDisplayCount"
            variant="outline"
            size="default"
            aria-haspopup="true"
            :aria-expanded="displayCountOpen"
          >
            <span>Show: {{ displayCount }}</span>
            <theme-icon icon="chevron-down-icon" type="icons" classes="flex" />
          </themed-button>
          
          <div v-if="displayCountOpen" class="themed-dropdown__menu" role="menu">
            <themed-button 
              v-for="option in pageSizeOptions"
              :key="option"
              @click="setDisplayCount(option)" 
              variant="ghost"
              size="sm"
              class="themed-dropdown__menu-item"
            >
              {{ option }}
            </themed-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Files Table -->
    <div class="table-container">
      <div v-if="error" class="error-state">
        {{ error }}
      </div>

      <themed-table
        ref="filesTable"
        :data="jobs"
        :columns="tableColumns"
        :searchable="false"
        :sortable="true"
        :pagination="paginationConfig"
        :per-page-options="[]"
        :striped="true"
        :hoverable="true"
        :loading="loading"
        aria-label="Legal AI Uploaded Files"
        @action="handleAction"
      >
        <template #column-input_file="{ row }">
          <span>
            {{ row.input_file || row.job_id || 'Unknown File' }}
          </span>
        </template>
        
        <template #column-status="{ row }">
          <span :class="['status-badge', getStatusClass(row.status)]">
            {{ getStatusLabel(row.status) }}
          </span>
        </template>
        
        <template #actions="{ row }">
          <span 
            v-if="row.status === 'done'"
            class="status-badge status-view"
            @click="handleAction({ action: 'view', row })"
            role="button"
            tabindex="0"
            @keydown.enter="handleAction({ action: 'view', row })"
            @keydown.space.prevent="handleAction({ action: 'view', row })"
          >
            View
          </span>
        </template>
      </themed-table>
    </div>
  </div>
</template>

<script>
import ThemedSearch from '../theme/v1/ThemedSearch.vue';
import ThemedButton from '../theme/v1/ThemedButton.vue';
import ThemeIcon from '../theme/v1/ThemeIcon.vue';

export default {
  name: 'LegalAiFilesTable',
  
  components: {
    ThemedSearch,
    ThemedButton,
    ThemeIcon
  },
  
  data() {
    return {
      searchQuery: '',
      statusFilter: '',
      displayCount: 10,
      displayCountOpen: false,
      pageSizeOptions: [5, 10, 20, 50],
      error: null,
      loading: false,
      allJobs: [],
      tableColumns: [
        {
          field: 'input_file',
          label: 'File Name',
          type: 'text',
          sortable: true,
          searchable: true
        },
        {
          field: 'status',
          label: 'Status',
          type: 'text',
          align:'left',
          sortable: true,
          searchable: true
        },
        {
          field: 'created_at',
          label: 'Uploaded',
          type: 'date',
          sortable: true,
          searchable: true,
          width:'10%',
          align:'center',
          format: 'MMM D, YYYY h:mm A'
        },
        {
          field: 'updated_at',
          label: 'Last Updated',
          type: 'date',
          width:'10%',
          sortable: true,
          searchable: true,
          align:'center',
          format: 'MMM D, YYYY h:mm A'
        },
        {
          type: 'actions',
          label: 'Actions',
          width: '100px',
          buttons: [
            {
              name: 'view',
              label: 'View',
              variant: 'default',
              size: 'sm',
              visible: (row) => row.status === 'done'
            }
          ]
        }
      ]
    };
  },

  computed: {
    jobs() {
      let filtered = this.allJobs;
      
      // Filter by status
      if (this.statusFilter) {
        filtered = filtered.filter(job => job.status === this.statusFilter);
      }
      
      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(job => {
          const fileName = (job.input_file || job.job_id || '').toLowerCase();
          const status = (job.status || '').toLowerCase();
          const createdAt = (job.created_at || '').toLowerCase();
          const updatedAt = (job.updated_at || '').toLowerCase();
          
          return fileName.includes(query) || 
                 status.includes(query) || 
                 createdAt.includes(query) || 
                 updatedAt.includes(query);
        });
      }
      
      return filtered;
    },
    
    paginationConfig() {
      return {
        perPage: this.displayCount
      };
    }
  },

  mounted() {
    this.fetchJobs();
  },

  methods: {
    async fetchJobs() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await window.axios.get('/lease-ai-files/all-data');
        
        if (response.data.success && response.data.jobs) {
          this.allJobs = response.data.jobs;
        } else {
          this.error = 'Failed to load files';
          this.allJobs = [];
        }
      } catch (error) {
        console.error('Error fetching jobs:', error);
        this.error = 'Error loading files. Please try again.';
        this.allJobs = [];
      } finally {
        this.loading = false;
      }
    },
    
    getStatusLabel(status) {
      const labels = {
        'processing': 'Processing',
        'done': 'Done',
        'queued': 'Queued',
        'failed': 'Failed',
        'pending': 'Pending'
      };
      return labels[status] || status.charAt(0).toUpperCase() + status.slice(1);
    },

    getStatusClass(status) {
      return `status-${status}`;
    },

    handleAction({ action, row }) {
      if (action === 'view') {
        window.location.href = `/lease-risk-ai?job_id=${row.job_id}`;
      }
    },

    handleStatusFilterChange() {
      // The filtering is now handled by the computed property
      // This method is kept for potential future enhancements
    },
    
    toggleDisplayCount() {
      this.displayCountOpen = !this.displayCountOpen;
    },
    
    setDisplayCount(count) {
      this.displayCount = count;
      this.displayCountOpen = false;
    },
    
    handlePerPageChange({ perPage }) {
      this.displayCount = perPage;
    },

    refresh() {
      // Reload data from API
      this.fetchJobs();
    }
  }
};
</script>
